import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    MenuItem,
    Pagination,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface ReceivingRecord {
    id: number;
    receiving_number: string;
    po_number: string;
    batch_number: string;
    vendor_name: string;
    receiving_date: string;
    status: string;
    created_at: string;
}

const ReceivingListPage: React.FC = () => {
    const navigate = useNavigate();
    const [records, setRecords] = useState<ReceivingRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });

    const limit = 20;

    useEffect(() => {
        fetchRecords();
    }, [page, filters]);

    const fetchRecords = async () => {
        try {
            setLoading(true);
            const params: any = { page, limit };
            if (filters.status) params.status = filters.status;

            const response = await apiClient.get('/receiving/list', { params });
            setRecords(response.data.receiving_records);
            setTotal(response.data.total);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch receiving records');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
            draft: 'default',
            completed: 'success',
            grading_started: 'primary',
            grn_generated: 'success'
        };
        return colors[status] || 'default';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Receiving Operations (L1)</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/inward/receiving/create')}
                >
                    New Receiving
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Search"
                            placeholder="Search by number, PO, batch..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            size="small"
                            sx={{ flex: 1 }}
                            InputProps={{
                                endAdornment: <SearchIcon />
                            }}
                        />
                        <TextField
                            label="Status"
                            select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="grading_started">Grading Started</MenuItem>
                        </TextField>
                    </Box>
                </CardContent>
            </Card>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Receiving #</TableCell>
                                    <TableCell>PO #</TableCell>
                                    <TableCell>Batch #</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Receiving Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.map((record) => (
                                    <TableRow key={record.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {record.receiving_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{record.po_number}</TableCell>
                                        <TableCell>
                                            <Chip label={record.batch_number} size="small" color="primary" />
                                        </TableCell>
                                        <TableCell>{record.vendor_name}</TableCell>
                                        <TableCell>
                                            {format(new Date(record.receiving_date), 'dd MMM yyyy')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={record.status.replace('_', ' ').toUpperCase()}
                                                size="small"
                                                color={getStatusColor(record.status)}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/inward/receiving/${record.id}`)}
                                                title="View Details"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={Math.ceil(total / limit)}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ReceivingListPage;
